/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { SVGProps } from 'react';

/**
 * Live BG's mobile-navigation hamburger, transcribed from live's own asset.
 *
 * WHY THIS IS AN ASSET AND NOT A BOX VALUE
 *
 * lucide's `Menu` — what this replaces — inks at a constant ~1.125 aspect at
 * EVERY box size, because its three rules are evenly spread across a square
 * 24-unit grid. Live BG's mark is a WIDE, SHORT stack at 1.774. No value of
 * `size-*` reaches that shape, so no token could have carried this; the fix had
 * to be the drawing. At the 30px box BG already declares, lucide's glyph paints
 * 23.0 x 20.0 — 3.3 too narrow and 5.2 too TALL.
 *
 * PROVENANCE — live's own file, not a measurement of it
 *
 * The `<path>` below is byte-identical to the `d` of the inline
 * `data:image/svg+xml;base64` asset on `img.mobile-navigation-hamburger-nav`,
 * decoded from four independent BG dumps in the evidence tree:
 * `.reference/bg-homepage.mobile.html`, `.reference/bg-homepage.desktop.html`,
 * `.reference/bg-search-dress.desktop.html` and
 * `.reference/bg-plp-womens-dresses.desktop.html`. All four carry the same 260
 * base64 characters. Live declares it `width="30" height="30"
 * viewBox="0 0 30 30"`, which is why the viewBox here is 30 and why BG's
 * existing `size-[30px]` box renders it at scale 1.
 *
 * The capture tree could not answer this: `.reference/captures/bg-home-m-375`
 * and `bg-search-m-375` carry the sibling magnifier as a decodable base64
 * background-image, but the decompiler REHOSTED the hamburger to a
 * storage.googleapis.com URL, so the glyph is absent from those files. The raw
 * dumps preserve live's own markup and were the only source that had it.
 *
 * GEOMETRY, read off the path — three FILLED rectangles, square ends, no caps
 * and no stroke:
 *
 *     rule     x            width    y                 thickness
 *     top      2 -> 28.336  26.336   8.001  ->  9.563  1.562
 *     middle   2 -> 28.336  26.336   14.636 -> 16.206  1.570
 *     bottom   2 -> 28.336  26.336   21.295 -> 22.843  1.548
 *
 * Ink box 26.336 x 14.842, aspect 1.774. Gaps 5.073 and 5.089 — even to within
 * 0.016. The stack sits 0.42 BELOW the box's vertical centre and 0.168 right of
 * its horizontal centre; both offsets are live's and are reproduced rather than
 * corrected, because the box is what positions the mark.
 *
 * TWO SOURCES AGREE. Two earlier agents pixel-scanned live's ink at 26.5 x 15.0
 * (aspect 1.767) through a colour-managed canvas off
 * `.reference/live-shots/bg-search-390.png` and `bg-plp-390.png` at DPR 2. This
 * asset's own numbers land 0.164 and 0.158 away from that scan — inside a
 * single device pixel — from a completely independent instrument. Neither
 * reading was derived from the other.
 *
 * `fill-rule="evenodd"` is live's own declaration. The three subpaths do not
 * overlap, so it changes nothing about the render; it is kept because this is a
 * transcription, and because `NmCartIcon` beside it carries the same pair.
 */
export function BgMenuIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 9.563h26.336V8.001H2zm0 13.28h26.336v-1.548H2zm0-6.637h26.336v-1.57H2z"
                fill="currentColor"
            />
        </svg>
    );
}
